import { Award, Users, Target, TrendingUp, MapPin } from 'lucide-react';
import { mockData } from '../mock';

export const About = () => {
  const stats = [
    { icon: Award, label: 'Year Established', value: '2024' },
    { icon: Users, label: 'Happy Clients', value: '50+' },
    { icon: Target, label: 'Projects Completed', value: '75+' },
    { icon: TrendingUp, label: 'Success Rate', value: '100%' }
  ];

  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-orange-600 font-semibold text-sm uppercase tracking-wider">
            About Us
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Building Excellence Since 2024
          </h2>
        </div>

        {/* Company Bio */}
        <div className="mb-16">
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-800 font-medium">
              A to Z Home Solutions is a premier, full-service interior design and home infrastructure firm dedicated to transforming residential and commercial spaces into functional works of art. True to our name, we provide a comprehensive, end-to-end suite of services—covering everything from the initial conceptual sketch to the final finishing touch.
            </p>
            
            <p>
              With a robust presence across Andhra Pradesh, Telangana, and Karnataka, we specialize in delivering high-quality, customized interiors that reflect the unique personality and lifestyle of our clients. Whether it is a modern urban apartment in Bangalore, a luxury villa in Hyderabad, or a family home in Andhra Pradesh, we bring local expertise and world-class design standards to every project.
            </p>

            {/* Why Choose Us */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl mt-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Us?</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg mb-1">Complete Lifecycle Management</h4>
                    <p className="text-gray-700">From "A to Z," we handle design, manufacturing, procurement, and execution. You don't have to juggle multiple vendors; we manage it all.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg mb-1">Regional Expertise</h4>
                    <p className="text-gray-700">Deeply rooted in the South Indian market, we understand local aesthetics, climate-specific material requirements, and cultural nuances.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg mb-1">Innovative Materials</h4>
                    <p className="text-gray-700">We stay ahead of the curve by utilizing durable, modern materials—including advanced WPC (Wood Plastic Composite) solutions—ensuring your home is as sustainable as it is beautiful.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">✓</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg mb-1">Client-Centric Philosophy</h4>
                    <p className="text-gray-700">Our founder-led team ensures that every project is treated with a personal touch, focusing on transparency, timely delivery, and unparalleled craftsmanship.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Core Services */}
            <div className="mt-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Core Services</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white border-2 border-orange-200 p-6 rounded-xl hover:shadow-lg transition-shadow duration-300">
                  <h4 className="font-bold text-orange-600 text-lg mb-3">1. Residential Interiors</h4>
                  <p className="text-gray-700">Custom modular kitchens, wardrobes, 3D living room visualizations, and themed children's bedrooms.</p>
                </div>
                
                <div className="bg-white border-2 border-orange-200 p-6 rounded-xl hover:shadow-lg transition-shadow duration-300">
                  <h4 className="font-bold text-orange-600 text-lg mb-3">2. Commercial Spaces</h4>
                  <p className="text-gray-700">Efficient and inspiring office layouts and retail interiors.</p>
                </div>
                
                <div className="bg-white border-2 border-orange-200 p-6 rounded-xl hover:shadow-lg transition-shadow duration-300">
                  <h4 className="font-bold text-orange-600 text-lg mb-3">3. End-to-End Execution</h4>
                  <p className="text-gray-700">Civil work, electrical, plumbing, and bespoke furniture manufacturing.</p>
                </div>
              </div>
            </div>

            <p className="text-xl text-center font-semibold text-orange-600 mt-8 italic">
              At A to Z Home Solutions, we don't just build interiors; we build the backdrop for your life's best moments.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="bg-gradient-to-br from-orange-50 to-orange-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-center"
              >
                <Icon className="text-orange-600 mb-4 mx-auto" size={40} />
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Service Locations */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl">
          <div className="flex items-start gap-4">
            <MapPin className="text-orange-600 flex-shrink-0 mt-1" size={32} />
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Service Locations</h3>
              <p className="text-gray-700 text-lg">
                We proudly serve clients across <span className="font-bold text-orange-600">
                  {mockData.company.serviceLocations.join(', ')}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};